import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Eye, KeyRound, Copy, RefreshCw, Shield } from "lucide-react";
import CustomerLayout from "../../../../layouts/CustomerLayout";
import Loader from "../../../../components/utilities/Loader";
import apiKeyRequests from "../../authenticated/senderIdManagement/ApiKeyRequests";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const ApiKey = () => {
  const [apiKey, setApiKey] = useState("");
  const [dateGenerated, setDateGenerated] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const navigate = useNavigate();
  const token = useSelector((state) => state.user?.token);

  // Format date and time to dd/mm/yy hh:mm
  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Fetch API key on mount
  useEffect(() => {
    const fetchApiKey = async () => {
      if (!token) {
        toast.error("Authentication token not found");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        const response = await apiKeyRequests.getApiKey(token);
        
        // Use the working data access pattern
        const res = response.data?.data || response.data || response;
        
        if (res?.status_code === "000" && !res?.in_error) {
          setApiKey(res.data?.api_key || "");
          setDateGenerated(res.point_in_time || "");
        } else {
          const errorMessage = res?.reason || res?.message || "Failed to load API key";
          toast.error(errorMessage);
          // //console.log("API Error:", res);
        }
      } catch (error) {
        toast.error(error.message || "Error fetching API key");
        // //console.log("Fetch API Key Error:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApiKey();
  }, [token]);

  // Handle regenerate key
  const handleRegenerate = async () => {
    if (!token) {
      toast.error("Authentication token not found");
      return;
    }

    setIsRegenerating(true);

    try {
      const response = await apiKeyRequests.regenerateApiKey(token);
      
      // Use the working data access pattern
      const res = response.data?.data || response.data || response;

      if (res?.status_code === "000" && !res?.in_error) {
        setApiKey(res.data?.api_key || "");
        setDateGenerated(res.point_in_time || "");
        toast.success("New API key generated successfully!");
      } else {
        const errorMessage = res?.reason || res?.message || "Failed to regenerate API key";
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error(error.message || "Error regenerating API key");
    } finally {
      setIsRegenerating(false);
    }
  };

  // Copy API key to clipboard
  const copyToClipboard = () => {
    if (!apiKey) return;
    
    navigator.clipboard.writeText(apiKey)
      .then(() => {
        toast.success("API key copied to clipboard!");
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(() => toast.error("Failed to copy API key"));
  };

  if (isLoading) return <Loader />;

  return (
    <CustomerLayout>
      <div className="pt-20 pb-10 min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-cyan-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6 md:mb-10">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              API Management
            </h1>
            <p className="text-gray-600 mt-1 md:mt-2 text-sm md:text-base">
              Manage your API keys and access documentation
            </p>
            <div className="h-1 w-16 md:w-20 bg-blue-600 mt-2 md:mt-4 rounded-full"></div>
          </div>

          {/* Desktop/Tablet View - Table Layout */}
          <div className="hidden md:block">
            <div className="overflow-x-auto bg-white border border-gray-200 rounded-2xl shadow-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-gray-700 font-semibold text-sm md:text-base">
                      API Key
                    </th>
                    <th className="px-6 py-4 text-left text-gray-700 font-semibold text-sm md:text-base">
                      Generated On
                    </th>
                    <th className="px-6 py-4 text-center text-gray-700 font-semibold text-sm md:text-base">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="font-mono text-gray-800 text-sm md:text-base break-all flex-1">
                          {apiKey || "No API key available"}
                        </div>
                        {apiKey && (
                          <button
                            onClick={copyToClipboard}
                            className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              isCopied 
                                ? "bg-green-100 text-green-700 border border-green-200" 
                                : "bg-blue-600 text-white hover:bg-blue-700"
                            }`}
                          >
                            {isCopied ? "Copied!" : "Copy"}
                          </button>
                        )}
                      </div>
                    </td>

                    <td className="px-6 py-4 text-gray-700 text-sm md:text-base">
                      {dateGenerated
                        ? formatDateTime(dateGenerated)
                        : "N/A"}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={() => navigate("/ApiDocumentation")}
                          className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl transition-colors text-sm font-medium"
                          title="View Documentation"
                        >
                          <Eye size={16} />
                          <span className="hidden sm:inline">Docs</span>
                        </button>

                        <button
                          onClick={handleRegenerate}
                          disabled={isRegenerating}
                          className={`flex items-center gap-2 px-4 py-2 ${
                            isRegenerating
                              ? "bg-yellow-100 text-yellow-700 cursor-not-allowed"
                              : "bg-yellow-50 hover:bg-yellow-100 text-yellow-700"
                          } rounded-xl transition-colors text-sm font-medium`}
                          title="Regenerate Key"
                        >
                          {isRegenerating ? (
                            <RefreshCw size={16} className="animate-spin" />
                          ) : (
                            <KeyRound size={16} />
                          )}
                          <span className="hidden sm:inline">
                            {isRegenerating ? "Regenerating..." : "Regenerate"}
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile View - Card Layout */}
          <div className="md:hidden">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">
                  API Key Details
                </h2>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <KeyRound size={14} />
                  <span>Secure Key</span>
                </div>
              </div>

              {/* API Key Display */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Key
                </label>
                <div className="bg-gray-50 border border-gray-300 rounded-xl p-3 mb-2">
                  <code className="font-mono text-gray-800 text-sm break-all">
                    {apiKey || "No API key available"}
                  </code>
                </div>
                {apiKey && (
                  <button
                    onClick={copyToClipboard}
                    className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isCopied 
                        ? "bg-green-100 text-green-700 border border-green-200" 
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    {isCopied ? (
                      <span className="flex items-center justify-center gap-2">
                        <Copy size={16} />
                        Copied to Clipboard!
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Copy size={16} />
                        Copy API Key
                      </span>
                    )}
                  </button>
                )}
              </div>

              {/* Date Generated */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Generated On
                </label>
                <div className="text-gray-700 bg-gray-50 border border-gray-300 rounded-xl p-3 text-sm">
                  {dateGenerated
                    ? formatDateTime(dateGenerated)
                    : "N/A"}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => navigate("/ApiDocumentation")}
                  className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl transition-colors text-sm font-medium"
                >
                  <Eye size={18} />
                  View API Documentation
                </button>

                <button
                  onClick={handleRegenerate}
                  disabled={isRegenerating}
                  className={`flex items-center justify-center gap-2 px-4 py-3 ${
                    isRegenerating
                      ? "bg-yellow-100 text-yellow-700 cursor-not-allowed"
                      : "bg-yellow-50 hover:bg-yellow-100 text-yellow-700"
                  } rounded-xl transition-colors text-sm font-medium`}
                >
                  {isRegenerating ? (
                    <>
                      <RefreshCw size={18} className="animate-spin" />
                      Regenerating Key...
                    </>
                  ) : (
                    <>
                      <KeyRound size={18} />
                      Regenerate API Key
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Security Notice - Responsive */}
          <div className="mt-6 md:mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-4 md:p-5">
            <div className="flex items-start gap-3 md:gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Shield size={18} className="text-blue-600 md:w-5 md:h-5" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1 md:mb-2 text-sm md:text-base">
                  Security Notice
                </h3>
                <p className="text-blue-700 text-xs md:text-sm leading-relaxed">
                  Your API key provides full access to your account. Keep it secure and never share it in publicly accessible areas such as GitHub, client-side code, or emails. If you suspect your key has been compromised, regenerate it immediately.
                </p>
              </div>
            </div>
          </div>

          {/* Documentation Link */}
          <div className="mt-6 md:mt-8 text-center">
            <p className="text-gray-600 text-sm">
              Need help? Check out our{" "}
              <button
                onClick={() => navigate("/ApiDocumentation")}
                className="text-blue-600 hover:text-blue-800 font-medium underline"
              >
                comprehensive API documentation
              </button>
            </p>
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default ApiKey;